// Complete translations dictionary and route configurations for Danish (da) and English (en)

export const languages = [
  { code: 'da', label: 'Dansk', shortLabel: 'DK', flag: '🇩🇰', isDefault: true },
  { code: 'en', label: 'English', shortLabel: 'EN', flag: '🇬🇧', isDefault: false },
];

export const routeMap = {
  home: {
    da: '/',
    en: '/en',
  },
  tastings: {
    da: '/vinsmagninger',
    en: '/en/wine-tastings',
  },
  lectures: {
    da: '/vinforedrag',
    en: '/en/wine-lectures',
  },
  about: {
    da: '/om-eliana',
    en: '/en/about-eliana',
  },
  contact: {
    da: '/kontakt',
    en: '/en/contact',
  },
};

// Helper to find the opposite language URL for any given pathname
export function getAlternateRoute(currentPath, targetLang) {
  const normalized = currentPath === '' ? '/' : currentPath;
  
  // Look up in routeMap
  for (const key of Object.keys(routeMap)) {
    const route = routeMap[key];
    if (route.da === normalized || (route.da !== '/' && normalized.startsWith(route.da))) {
      return route[targetLang] || route.da;
    }
    if (route.en === normalized || (route.en !== '/en' && normalized.startsWith(route.en))) {
      return route[targetLang] || route.da;
    }
  }

  // Handle root /en or /
  if (normalized === '/' && targetLang === 'en') return '/en';
  if (normalized === '/en' && targetLang === 'da') return '/';
  if (normalized.startsWith('/da/')) {
    const daSub = normalized.replace(/^\/da/, '');
    return getAlternateRoute(daSub || '/', targetLang);
  }

  return targetLang === 'en' ? '/en' : '/';
}

export const translations = {
  da: {
    meta: {
      siteName: 'Eliana Wine',
      home: {
        title: 'Eliana Wine | Eksklusive Vinsmagninger & Vinforedrag',
        description: 'Professionelle vinsmagninger og inspirerende vinforedrag med over 20 års erfaring. Oplev, forstå og nyd vin uden snobberi.',
      },
      tastings: {
        title: 'Vinsmagninger | Eksklusive Oplevelser - Eliana Wine',
        description: 'Skræddersyede og personlige vinsmagninger for private og erhverv. Oplev nøje udvalgte vine og lær at smage med professionel vejledning.',
      },
      lectures: {
        title: 'Vinforedrag | Levende Formidling & Vinviden - Eliana Wine',
        description: 'Fascinerende vinforedrag om vinregioner, historie, gastronomi og produktion. Faglig dybde og passioneret formidling i øjenhøjde.',
      },
      about: {
        title: 'Om Eliana Napoli | Sommelier & Wine Ambassador - Eliana Wine',
        description: 'Lær mere om Eliana – uddannet sommelier med bronzemedalje, WSET studerende og mere end 20 års dedikation til vinens verden.',
      },
      contact: {
        title: 'Kontakt | Book Vinsmagning eller Foredrag - Eliana Wine',
        description: 'Kontakt Eliana Wine for forespørgsler og booking af personlige vinsmagninger eller faglige vinforedrag.',
      },
    },
    nav: [
      { name: 'Vinsmagninger', href: '/vinsmagninger' },
      { name: 'Vinforedrag', href: '/vinforedrag' },
      { name: 'Om Eliana Wine', href: '/om-eliana' },
      { name: 'Kontakt', href: '/kontakt' },
    ],
    common: {
      experienceBadge: 'Experience / 20+ Years in Wine',
      menuOpen: 'Menu',
      menuClose: 'Luk',
      contactUs: 'Kontakt os',
      readMore: 'Læs mere',
      bookExperience: 'Book en vinoplevelse',
      seeOptions: 'Se mulighederne',
      yearsExperience: 'Års vinerfaring',
      sendInquiry: 'Send Forespørgsel',
      sending: 'Sender...',
      thankYouMessage: 'Tak for din henvendelse! Jeg vender tilbage hurtigst muligt.',
      errorMessage: 'Der skete en fejl. Prøv venligst igen eller send en direkte e-mail.',
      name: 'Navn',
      email: 'Email',
      phone: 'Telefon',
      subject: 'Emne',
      message: 'Besked',
      location: 'Placering',
      followUs: 'Følg',
      rights: 'Alle rettigheder forbeholdes.',
      terms: 'Betingelser',
      privacy: 'Privatliv',
      cookies: 'Cookies',
    },
    contactInfo: {
      email: 'eliananapoli.wines@gmail.com',
      phone: '52 90 02 80',
      address: 'Bårup postnr: 4360',
      cvr: '40990321',
      locationText: 'Bårup / København, Danmark',
    },
    home: {
      hero: {
        titleLine1: 'Vin skal opleves,',
        titleLine2: 'forstås og nydes',
        badge: 'Over 20 års erfaring i vinbranchen',
        description: 'Vinsmagninger og vinforedrag med faglig dybde, personlig formidling og fokus på den fuldendte oplevelse omkring vinen.',
        ctaTastings: 'Udforsk Vinsmagninger',
        ctaLectures: 'Udforsk Vinforedrag',
        image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/44120024-e095-446b-8ee7-1dea28fabce6.jpg',
      },
      intro: {
        badge: 'Eksklusive Vinoplevelser',
        title: 'Vinforedrag og vinsmagninger med faglig dybde og nærvær',
        text: 'Gennem personlig og engagerende formidling samt en dyb faglig indsigt skaber jeg rammerne for uforglemmelige vinoplevelser. Vin er mere end teknik og druer; det er historier, kultur, håndværk og fælles nydelse.',
        cta: 'LÆS MERE OM MIG',
      },
      aboutSection: {
        badge: 'OM MIG',
        quote: 'Wine Caught Me.',
        title: 'Mød Eliana Napoli',
        subtitle: 'Sommelier & Wine Ambassador',
        lead: 'Jeg har en baggrund i litteratur, hvor ord, historier og kommunikation altid har været en stor del af mit liv. Vinen kom senere – men den fangede mig.',
        p1: 'Jeg fandt min vej ind i vinens verden gennem gastronomien som uddannet tjener og sommelier på Danmarks bedste restauranter, og senere som vinkonsulent for fine wines.',
        p2: '',
        today: 'I dag arbejder jeg som **Wine Ambassador hos Danmarks største vinproducent** og har samarbejdet med anerkendte italienske organisationer som **Barolo & Friends** og **Consorzio della Valpolicella**.',
        philosophy: 'For mig handler vin om mere end det, der er i glasset – det handler om mennesker, historier og de gode samtaler.',
        closing: '',
        cta: 'Læs hele min historie',
        image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/fotograf-steen-knarberg-20260827-dsc0561-srgb-2500pix_ae5348c0.jpeg',
        experienceBadge: '20+ års vinerfaring',
        highlights: [
          { title: 'Uddannet Sommelier & Tjener', text: 'Baggrund fra Danmarks bedste restauranter' },
          { title: 'Barolo & Valpolicella', text: 'Samarbejde med anerkendte italienske konsortier' },
          { title: 'Wine Ambassador', text: 'Hos Danmarks største vinproducent' },
        ],
      },
      services: {
        tastings: {
          badge: 'PERSONLIG & ENGAGERENDE SMAGNING',
          title: 'Vinsmagninger',
          description: 'Personlige, professionelle og engagerende vinoplevelser, hvor formidling, historier, smag og forståelse for vinen er i absolut centrum.',
          quoteBadge: 'OPLEVELSENS ESSENS',
          quote: 'Sand forståelse for vin opstår, når man opdager historien og nuancerne bag hver enkelt dråbe.',
          cta: 'SE VINSMAGNINGER',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg',
        },
        lectures: {
          badge: 'LEVENDE & FAGLIG FORMIDLING',
          title: 'Vinforedrag',
          description: 'Levende og faglig formidling omkring vin, vinområder, producenter, kultur, gastronomi og de fascinerende historier bag flaskerne.',
          quoteBadge: 'FORMIDLING I ØJENHØJDE',
          quote: 'At gøre det komplekse gribeligt og spændende – helt uden snobberi.',
          cta: 'SE VINFOREDRAG',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/178dabb9-6b5e-454d-ad5e-6b83fa377544.jpg',
        },
      },
      experience: {
        badge: 'Udbytte',
        titleLine1: 'Det du kan',
        titleLine2: 'forvente',
        items: [
          {
            title: 'Faglig Indsigt',
            text: 'En dybere forståelse for vinens verden, fra terroir til teknik.',
          },
          {
            title: 'Sanselig Læring',
            text: 'Evnen til at sætte ord på smagsoplevelser og duftnuancer.',
          },
          {
            title: 'Fælles Historier',
            text: 'En fælles oplevelse der skaber samtaler og minder.',
          },
          {
            title: 'Bedre Vinforståelse',
            text: 'Redskaberne til at vælge og nyde bedre vin i fremtiden.',
          },
        ],
        stat1Number: '100%',
        stat1Label: 'Fokus på kvalitet',
        stat2Number: '20+',
        stat2Label: 'Års vinerfaring',
        cta: 'Book din vinoplevelse',
      },
      contactSection: {
        titleLine1: 'Lad os planlægge',
        titleLine2: 'din vinoplevelse',
        subtitle: 'Klar til en inspirerende vinsmagning eller et engagerende foredrag? Kontakt mig for en uforpligtende samtale.',
        formSubjectOptions: ['Vinsmagning', 'Vinforedrag', 'Andet'],
      },
    },
    tastingsPage: {
      badge: 'Eksklusive Vinoplevelser',
      heroTitle1: 'Smag på',
      heroTitle2: 'øjeblikket & historien',
      heroSubtitle: 'Personlige, professionelle og engagerende vinsmagninger, hvor formidling, historie, smag og forståelse for vin er i absolut centrum.',
      heroCardNumber: '100%',
      heroCardText: 'Personlig og nærværende smagsoplevelse',
      tastingTypesBadge: 'Smagning',
      requestButton: 'Forespørg smagning',
      types: [
        {
          number: '01',
          title: 'LÆR AT SMAGE PÅ VIN',
          description: 'Lær de grundlæggende teknikker, forstå aromaer, syre-balance og tanniner, og find din personlige smag og vinpræferencer.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/44120024-e095-446b-8ee7-1dea28fabce6.jpg',
        },
        {
          number: '02',
          title: 'MIN ITALIENSKE VINVERDEN',
          description: 'En passioneret rejse gennem Italiens store vinregioner – fra Barolo og Piemonte til Valpolicella, Amarone og klassiske vinhuse.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg',
        },
        {
          number: '03',
          title: 'NYE VINOMRÅDER AT OPDAGE',
          description: 'Gå på opdagelse i spændende, up-and-coming vinregioner, nye trends, kølige klimaer og nytænkende producenter fra hele verden.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/4b48daac-7874-4af2-bb70-1c8c51ac136e.jpg',
        },
        {
          number: '04',
          title: 'SKRÆDDERSYET TIL DIG',
          description: 'En fuldstændig skræddersyet smagning sammensat ud fra jeres ønsker, anledning og præferencer – uanset niveau og selskabsstørrelse.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/a3c32649-b3b1-4739-b59f-053212e193c0.jpg',
        },
      ],
      philosophyBadge: 'Tilgang & Filosofi',
      philosophyTitle: 'Hvad gør en vinsmagning med Eliana speciel?',
      philosophyP1: 'En god vinsmagning handler ikke om indviklede fagudtryk, men om at åbne sanserne. Jeg skaber en afslappet, engagerende stemning, hvor alle kan være med – uanset om du er nysgerrig nybegynder eller erfaren vinkender.',
      philosophyP2: 'Hver smagning tilpasses selskabets ønsker, hvad enten det er til et privat arrangement, en firmaaften eller en særlig mærkedag.',
      galleryBadge: 'Glimt fra smagninger',
      galleryTitle: 'Stemningsbilleder',
      gallerySubtitle: 'Klik på billederne for at se dem i stor visning',
      galleryImages: [
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3199.jpg',
          title: 'Nærvær & Formidling',
          category: 'Stemning',
          description: 'Levende fortællinger og faglig dybde i en uformel og indbydende atmosfære.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3270.jpg',
          title: 'Eksklusive Vine & Omgivelser',
          category: 'Vinsmagning',
          description: 'Nøje udvalgte årgange og terroir-drevne vine skænket i de rette rammer.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/1784116466617.jpeg',
          title: 'Smagsoplevelser i Fokus',
          category: 'Sensorik',
          description: 'Lær at analysere farve, bouquet, syre og krop med professionelle teknikker.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3271.jpeg',
          title: 'Detaljer & Perfektion',
          category: 'Gastronomi',
          description: 'Harmoniske sammensætninger og sans for detaljen ved hver eneste servering.',
        },
      ],
      ctaTitle1: 'Klar til en',
      ctaTitle2: 'eksklusiv vinsmagning?',
      ctaButton: 'Book din smagning i dag',
    },
    lecturesPage: {
      badge: 'Inspiration & Levende Formidling',
      heroTitle1: 'Vinforedrag med',
      heroTitle2: 'faglig dybde og fortællelyst',
      imageAlt: 'Vinforedrag',
      essenceTitle: 'Essensen af foredraget',
      essenceText: 'Levende og faglig formidling omkring vin, vinområder, producenter, kultur, gastronomi og historierne bag vinene.',
      bookButton: 'Book et foredrag',
      contentTitle: 'Formidling i øjenhøjde',
      topics: [
        { title: 'Regioner & Druer', description: 'En rejse gennem verdens vigtigste vindistrikter, klimaer og druesorter.' },
        { title: 'Vinens Historie & Kultur', description: 'Fra antikken og middelalderens munke til moderne produktionsteknikker.' },
        { title: 'Smag & Forståelse', description: 'Hvordan lærer man at smage vin som en professionel og finde balancen?' },
      ],
      quote: 'At lytte til et foredrag om vin skal være som at drikke en god flaske – man skal føle sig beriget, inspireret og en smule klogere, når man går derfra.',
      paragraph1: 'Vores vinforedrag er designet til at vække nysgerrighed. Vi taler om kultur, historie, gastronomi, producenter og de unikke historier, der ligger bag hver dråbe. Det er en engagerende rejse præsenteret med faglig tyngde og passion.',
      paragraph2: 'Vi tilpasser indholdet til jeres ønsker og forudsætninger, så foredraget giver størst mulig værdi og oplevelse for alle deltagere.',
      ctaTitle1: 'Klar til et',
      ctaTitle2: 'inspirerende foredrag?',
      ctaButton: 'Book dit vinforedrag',
    },
    aboutPage: {
      badge: 'Om Eliana',
      title: 'OM MIG',
      quote: 'Wine Caught Me.',
      intro: 'Jeg har en baggrund i litteratur, hvor ord, historier og kommunikation altid har været en stor del af mit liv.',
      introSub: 'Vinen kom senere – men den fangede mig.',
      p1: 'Jeg startede fra bunden og fandt min vej ind i vinens verden gennem gastronomien. Jeg er uddannet tjener og sommelier og har arbejdet på nogle af Danmarks bedste restauranter, senere som vinkonsulent med fokus på kvalitets- og fine wines.',
      p2Intro: 'Undervejs har jeg haft fornøjelsen af at arbejde med nogle af Italiens anerkendte vinorganisationer og konsortier.',
      baroloLabel: 'Barolo & Friends',
      baroloText: 'Som en del af **Barolo & Friends** har jeg arbejdet med at præsentere Barolo og vine fra Piemonte på det danske marked.',
      valpolicellaLabel: 'Consorzio della Valpolicella',
      valpolicellaText: 'Jeg har også samarbejdet med **Consorzio della Valpolicella**, hvor jeg blandt andet har præsenteret Amarone i Politikens Hus i København.',
      p3Highlight: 'Wine Ambassador hos Danmarks største vinproducent',
      p3Text: 'I dag arbejder jeg som **Wine Ambassador hos Danmarks største vinproducent**, hvor jeg kombinerer min passion for vin med mennesker, salg og oplevelser. Samtidig bruger jeg stadig meget tid på at lære mere om vin – ikke mindst om vinproduktion og det håndværk, der ligger bag.',
      philosophyTitle: 'For mig handler vin nemlig om mere end det, der er i glasset.',
      philosophyText: 'Det handler om mennesker, steder, historier og de gode samtaler, der opstår omkring et glas vin.',
      closingHighlight: 'Jeg håber, at jeg kan dele min glæde og passion for vin med dig – og måske inspirere dig til at opdage en ny vin, en ny historie eller bare nyde et godt glas.',
      yearsBadge: '20+',
      yearsText: 'Års vinerfaring',
      ctaButton: 'Kontakt mig',
      passionCardTitle: 'Passioneret Formidling',
      passionCardSub: 'Skaber minder gennem hver dråbe',
    },
    contactPage: {
      badge: 'Kontakt & Forespørgsler',
      heroTitle1: 'Lad os',
      heroTitle2: 'tale om vin',
      heroSubtitle: 'Hvad enten du ønsker at booke en vinsmagning eller et vinforedrag, er du altid velkommen til at kontakte mig for en uforpligtende samtale.',
      infoTitle: 'Kontaktinformation',
      emailLabel: 'Send en mail',
      phoneLabel: 'Giv et kald',
      addressLabel: 'Lokation',
      cvrLabel: 'Virksomhed',
      experienceBoxNumber: '20+ år',
      experienceBoxText: 'Erfaring i vinbranchen til din tjeneste',
      formTitle: 'Send en forespørgsel',
      formName: 'Dit navn',
      formEmail: 'Din email',
      formSubject: 'Hvad drejer henvendelsen sig om?',
      formSubjectDefault: 'Vælg et emne',
      formOptions: ['Vinsmagning', 'Vinforedrag', 'Andet arrangement / Rådgivning'],
      formMessage: 'Fortæl om dit arrangement, ønsket dato og antal deltagere',
      formSubmit: 'Send Forespørgsel',
      formSubmitting: 'Sender forespørgsel...',
      formSuccess: 'Mange tak for din henvendelse! Jeg glæder mig til at vende tilbage til dig snarest.',
      formError: 'Der opstod desværre en fejl. Send venligst en email direkte til eliananapoli.wines@gmail.com.',
    },
    navbar: {
      expertiseBadge: 'Expertise & Baggrund',
      exp1: 'Over 20 års erfaring i vinbranchen',
      exp2: 'Sommelier & Tjenereksamen med bronzemedalje',
      exp3: 'Wine Ambassador for Danmarks største vinproducent',
    },
    footer: {
      tagline: 'Professionelle vinoplevelser med fokus på faglighed, passion og personlig formidling.',
      navTitle: 'Naviger',
      contactTitle: 'Kontakt',
      newsletterTitle: 'Nyhedsbrev',
      newsletterText: 'Tilmeld dig for invitationer til eksklusive smagninger og foredrag.',
      newsletterPlaceholder: 'Din email',
      newsletterButton: 'Tilmeld',
      copyright: `© ${new Date().getFullYear()} Eliana Wine. Alle rettigheder forbeholdes.`,
    },
  },

  en: {
    meta: {
      siteName: 'Eliana Wine',
      home: {
        title: 'Eliana Wine | Exclusive Wine Tastings & Lectures in Denmark',
        description: 'Professional wine tastings and inspiring wine lectures with over 20 years of experience. Experience, understand and enjoy wine without snobbery.',
      },
      tastings: {
        title: 'Wine Tastings | Exclusive Tasting Experiences - Eliana Wine',
        description: 'Bespoke and engaging wine tastings for private and corporate events. Discover carefully selected fine wines with professional sommelier guidance.',
      },
      lectures: {
        title: 'Wine Lectures | Inspiring Wine Knowledge & Stories - Eliana Wine',
        description: 'Fascinating wine lectures on world wine regions, history, gastronomy and winemaking. In-depth professional expertise presented with passion.',
      },
      about: {
        title: 'About Eliana Napoli | Sommelier & Wine Ambassador - Eliana Wine',
        description: 'Meet Eliana – certified sommelier with a bronze medal distinction, WSET Diploma candidate, and over 20 years of dedication to the world of wine.',
      },
      contact: {
        title: 'Contact | Book a Wine Tasting or Lecture - Eliana Wine',
        description: 'Contact Eliana Wine for bookings and inquiries regarding bespoke wine tastings and inspiring wine lectures.',
      },
    },
    nav: [
      { name: 'Wine Tastings', href: '/en/wine-tastings' },
      { name: 'Wine Lectures', href: '/en/wine-lectures' },
      { name: 'About Eliana Wine', href: '/en/about-eliana' },
      { name: 'Contact', href: '/en/contact' },
    ],
    common: {
      experienceBadge: 'Experience / 20+ Years in Wine',
      menuOpen: 'Menu',
      menuClose: 'Close',
      contactUs: 'Contact Us',
      readMore: 'Read More',
      bookExperience: 'Book a Wine Experience',
      seeOptions: 'Explore Options',
      yearsExperience: 'Years of Wine Experience',
      sendInquiry: 'Send Inquiry',
      sending: 'Sending...',
      thankYouMessage: 'Thank you for reaching out! I will get back to you shortly.',
      errorMessage: 'An error occurred. Please try again or send a direct email.',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Subject',
      message: 'Message',
      location: 'Location',
      followUs: 'Follow',
      rights: 'All rights reserved.',
      terms: 'Terms',
      privacy: 'Privacy',
      cookies: 'Cookies',
    },
    contactInfo: {
      email: 'eliananapoli.wines@gmail.com',
      phone: '+45 52 90 02 80',
      address: 'Bårup Postal Code: 4360',
      cvr: 'DK-40990321',
      locationText: 'Bårup / Copenhagen, Denmark',
    },
    home: {
      hero: {
        titleLine1: 'Wine should be experienced,',
        titleLine2: 'understood & enjoyed',
        badge: 'Over 20 years of experience in the wine world',
        description: 'Wine tastings and wine lectures with profound expertise, engaging storytelling, and focus on the complete enjoyment of wine.',
        ctaTastings: 'Explore Wine Tastings',
        ctaLectures: 'Explore Wine Lectures',
        image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/44120024-e095-446b-8ee7-1dea28fabce6.jpg',
      },
      intro: {
        badge: 'Exclusive Wine Experiences',
        title: 'Wine lectures and tastings with professional depth and authentic presence',
        text: 'Through personal and engaging communication combined with profound expertise, I craft unforgettable wine experiences. Wine is far more than technical details and grapes; it is history, culture, craft, and shared joy.',
        cta: 'READ MORE ABOUT ME',
      },
      aboutSection: {
        badge: 'ABOUT ME',
        quote: 'Wine Caught Me.',
        title: 'Meet Eliana Napoli',
        subtitle: 'Sommelier & Wine Ambassador',
        lead: 'I have a background in literature, where words, stories, and communication have always been a central part of my life. Wine came later – but it caught me completely.',
        p1: 'I found my path into the wine world through gastronomy as a trained waiter and certified sommelier at some of Denmark’s finest restaurants, later consulting on fine wines.',
        p2: '',
        today: 'Today, I work as **Wine Ambassador at Denmark’s largest wine producer** and have collaborated with renowned Italian wine consortia such as **Barolo & Friends** and **Consorzio della Valpolicella**.',
        philosophy: 'To me, wine is about more than what is in the glass – it is about people, places, stories, and the great conversations that unfold around a glass of wine.',
        closing: '',
        cta: 'Read my full story',
        image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/fotograf-steen-knarberg-20260827-dsc0561-srgb-2500pix_ae5348c0.jpeg',
        experienceBadge: '20+ years wine experience',
        highlights: [
          { title: 'Certified Sommelier & Waiter', text: 'Background from Denmark’s premier restaurants' },
          { title: 'Barolo & Valpolicella', text: 'Collaborations with esteemed Italian wine consortia' },
          { title: 'Wine Ambassador', text: 'At Denmark’s largest wine producer' },
        ],
      },
      services: {
        tastings: {
          badge: 'PERSONAL & ENGAGING TASTING',
          title: 'Wine Tastings',
          description: 'Personal, professional and captivating tasting sessions where stories, flavours and a deep understanding of wine take center stage.',
          quoteBadge: 'ESSENCE OF THE EXPERIENCE',
          quote: 'True understanding of wine emerges when you uncover the story and subtleties behind every single drop.',
          cta: 'EXPLORE WINE TASTINGS',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg',
        },
        lectures: {
          badge: 'LIVELY & IN-DEPTH STORYTELLING',
          title: 'Wine Lectures',
          description: 'Inspiring and educational presentations on wine regions, producers, terroir, gastronomy and the fascinating history behind great bottles.',
          quoteBadge: 'APPROACHABLE EXPERTISE',
          quote: 'Making the complex accessible and exhilarating – completely free of snobbery.',
          cta: 'EXPLORE WINE LECTURES',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/178dabb9-6b5e-454d-ad5e-6b83fa377544.jpg',
        },
      },
      experience: {
        badge: 'Key Takeaways',
        titleLine1: 'What you can',
        titleLine2: 'expect',
        items: [
          {
            title: 'Professional Insight',
            text: 'A deeper understanding of the wine world, from terroir to winemaking techniques.',
          },
          {
            title: 'Sensory Learning',
            text: 'The ability to articulate aroma profiles, palate structure and nuanced tasting notes.',
          },
          {
            title: 'Shared Memories',
            text: 'A memorable collective experience that sparks inspiring conversations.',
          },
          {
            title: 'Refined Appreciation',
            text: 'The tools and confidence to choose, pair and enjoy superior wines going forward.',
          },
        ],
        stat1Number: '100%',
        stat1Label: 'Focus on Quality',
        stat2Number: '20+',
        stat2Label: 'Years Wine Expertise',
        cta: 'Book Your Wine Experience',
      },
      contactSection: {
        titleLine1: "Let's plan your",
        titleLine2: 'wine experience',
        subtitle: 'Ready for an inspiring tasting or an engaging lecture? Get in touch for a friendly and non-binding conversation.',
        formSubjectOptions: ['Wine Tasting', 'Wine Lecture', 'Other Inquiries'],
      },
    },
    tastingsPage: {
      badge: 'Exclusive Wine Experiences',
      heroTitle1: 'Taste the',
      heroTitle2: 'moment & the story',
      heroSubtitle: 'Personal, professional and captivating wine tastings where storytelling, authenticity, flavours and genuine understanding of wine take center stage.',
      heroCardNumber: '100%',
      heroCardText: 'Personal and authentic tasting experience',
      tastingTypesBadge: 'Tasting',
      requestButton: 'Request Tasting',
      types: [
        {
          number: '01',
          title: 'LEARN TO TASTE WINE',
          description: 'Master fundamental tasting techniques, understand aromas, acidity-structure balance, and discover your personal palate.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/44120024-e095-446b-8ee7-1dea28fabce6.jpg',
        },
        {
          number: '02',
          title: 'MY ITALIAN WINE WORLD',
          description: 'A passionate journey through Italy’s greatest wine regions – from Barolo and Piedmont to Valpolicella, Amarone, and artisan estates.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/921469fa-4fa3-4d11-a961-032532a857eb.jpg',
        },
        {
          number: '03',
          title: 'NEW WINE REGIONS TO DISCOVER',
          description: 'Explore exciting emerging wine regions, cool-climate terroirs, modern trends, and visionary winemakers.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/4b48daac-7874-4af2-bb70-1c8c51ac136e.jpg',
        },
        {
          number: '04',
          title: 'TAILORED FOR YOU',
          description: 'A completely bespoke tasting tailored around your wishes, theme, occasion, and level – ideal for private gatherings and corporate events.',
          image: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/a3c32649-b3b1-4739-b59f-053212e193c0.jpg',
        },
      ],
      philosophyBadge: 'Approach & Philosophy',
      philosophyTitle: 'What makes a tasting with Eliana exceptional?',
      philosophyP1: 'A great wine tasting is not about convoluted jargon, but about awakening your senses. I foster a relaxed, vibrant atmosphere where everyone feels welcome – whether you are a curious beginner or a seasoned collector.',
      philosophyP2: 'Each tasting is custom-tailored to your party’s preferences, whether for private gatherings, corporate entertainment or special celebrations.',
      galleryBadge: 'Atmospheric Moments',
      galleryTitle: 'Gallery & Highlights',
      gallerySubtitle: 'Click on any image to view in full resolution',
      galleryImages: [
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3199.jpg',
          title: 'Presence & Storytelling',
          category: 'Atmosphere',
          description: 'Engaging tales and professional depth in a warm, welcoming setting.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3270.jpg',
          title: 'Fine Wines & Ambience',
          category: 'Wine Tasting',
          description: 'Carefully curated vintages and terroir-driven wines served in perfect harmony.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/1784116466617.jpeg',
          title: 'Sensory Exploration',
          category: 'Sensory Skills',
          description: 'Learn how to analyze color, bouquet, acidity, and structure using master sommelier techniques.',
        },
        {
          url: 'https://assets.auxodev.dk/assets/b835980a-208f-4a2a-a4d0-0b3689e0c3e6/img-3271.jpeg',
          title: 'Detail & Perfection',
          category: 'Gastronomy',
          description: 'Harmonious pairings and meticulous attention to every pour.',
        },
      ],
      ctaTitle1: 'Ready for an',
      ctaTitle2: 'exclusive wine tasting?',
      ctaButton: 'Book your tasting today',
    },
    lecturesPage: {
      badge: 'Inspiration & Engaging Knowledge',
      heroTitle1: 'Wine lectures with',
      heroTitle2: 'expert depth and passion',
      imageAlt: 'Wine Lecture',
      essenceTitle: 'Essence of the Lecture',
      essenceText: 'Lively and educational storytelling covering world wine regions, historical lineages, culinary pairings, and the heritage behind great vintages.',
      bookButton: 'Book a Lecture',
      contentTitle: 'Accessible Expertise',
      topics: [
        { title: 'Regions & Grape Varieties', description: 'A journey through the world’s preeminent wine terroirs, microclimates and varietals.' },
        { title: 'History & Culture of Wine', description: 'From ancient origins to monastic traditions and contemporary vinification.' },
        { title: 'Tasting & Sensory Mastery', description: 'How to decode fine wine like a professional and discern quality and balance.' },
      ],
      quote: 'Listening to a wine lecture should be like opening a great bottle – you should leave enriched, inspired and a little wiser.',
      paragraph1: 'Our wine lectures are created to spark genuine curiosity. We delve into culture, history, gastronomy, producers, and the human stories behind every drop. It is a captivating journey delivered with authoritative depth and contagious enthusiasm.',
      paragraph2: 'We tailor the format and content to your audience’s background and objectives, ensuring maximum engagement and value for every participant.',
      ctaTitle1: 'Ready for an',
      ctaTitle2: 'inspiring wine lecture?',
      ctaButton: 'Book your wine lecture',
    },
    aboutPage: {
      badge: 'About Eliana',
      title: 'ABOUT ME',
      quote: 'Wine Caught Me.',
      intro: 'I have a background in literature, where words, stories, and communication have always been a large part of my life.',
      introSub: 'Wine came later – but it caught me.',
      p1: 'I started from scratch and found my way into the world of wine through gastronomy. I am a trained waiter and sommelier and have worked at some of Denmark’s best restaurants, later as a wine consultant focusing on quality and fine wines.',
      p2Intro: "Along the way, I've had the pleasure of working with some of Italy's renowned wine organizations and consortia.",
      baroloLabel: 'Barolo & Friends',
      baroloText: 'As part of **Barolo & Friends**, I worked to present Barolo and wines from Piedmont on the Danish market.',
      valpolicellaLabel: 'Consorzio della Valpolicella',
      valpolicellaText: 'I have also collaborated with **Consorzio della Valpolicella**, where I presented Amarone at Politikens Hus in Copenhagen.',
      p3Highlight: "Wine Ambassador for Denmark's largest wine producer",
      p3Text: "Today, I work as a **Wine Ambassador for Denmark's largest wine producer**, where I combine my passion for wine with people, sales, and experiences. At the same time, I still spend a lot of time learning more about wine – not least about wine production and the craft behind it.",
      philosophyTitle: 'To me, wine is about much more than what is in the glass.',
      philosophyText: 'It is about people, places, stories, and the good conversations that unfold over a glass of wine.',
      closingHighlight: 'I hope that I can share my joy and passion for wine with you – and perhaps inspire you to discover a new wine, a new story, or simply enjoy a great glass.',
      yearsBadge: '20+',
      yearsText: 'Years Wine Experience',
      ctaButton: 'Contact me',
      passionCardTitle: 'Passionate Storytelling',
      passionCardSub: 'Creating lasting memories with every pour',
    },
    contactPage: {
      badge: 'Contact & Inquiries',
      heroTitle1: 'Let us',
      heroTitle2: 'talk about wine',
      heroSubtitle: 'Whether you wish to book a bespoke wine tasting or an inspiring lecture, you are always welcome to reach out for a non-binding conversation.',
      infoTitle: 'Contact Information',
      emailLabel: 'Send an email',
      phoneLabel: 'Give a call',
      addressLabel: 'Location',
      cvrLabel: 'Business Registration',
      experienceBoxNumber: '20+ years',
      experienceBoxText: 'Wine industry experience at your service',
      formTitle: 'Send an inquiry',
      formName: 'Your Name',
      formEmail: 'Your Email',
      formSubject: 'Subject of your inquiry',
      formSubjectDefault: 'Select an option',
      formOptions: ['Wine Tasting', 'Wine Lecture', 'Other Event / Consulting'],
      formMessage: 'Tell me about your event, preferred date and number of guests',
      formSubmit: 'Send Inquiry',
      formSubmitting: 'Sending inquiry...',
      formSuccess: 'Thank you very much for your inquiry! I look forward to getting back to you shortly.',
      formError: 'An error occurred. Please send an email directly to eliananapoli.wines@gmail.com.',
    },
    navbar: {
      expertiseBadge: 'Expertise & Background',
      exp1: 'Over 20 years of experience in the wine industry',
      exp2: 'Certified Sommelier & Waiter with bronze medal',
      exp3: 'Wine Ambassador for Denmark’s largest wine producer',
    },
    footer: {
      tagline: 'Professional wine experiences centered around expertise, passion and engaging communication.',
      navTitle: 'Navigation',
      contactTitle: 'Contact',
      newsletterTitle: 'Newsletter',
      newsletterText: 'Subscribe for invitations to exclusive tastings and masterclasses.',
      newsletterPlaceholder: 'Your email address',
      newsletterButton: 'Subscribe',
      copyright: `© ${new Date().getFullYear()} Eliana Wine. All rights reserved.`,
    },
  },
};
